/**
 * Warning acknowledgement (2026-10-06) — the pure half, shared by ora and the
 * Outlook add-in (one UI standard).
 *
 * Joachim: "I need every user to acknowledge each problem before we call it
 * good." A finished run with any open warning line reads "Completed*" (amber);
 * once every line has been acknowledged — by anyone, once — it reads
 * "Completed". The lines and who acknowledged each come from the status payload
 * (`warning_items`, substrate `types/run_warnings.py`): the early-exit reason,
 * then a completed run's legacy `metadata.warning` as `metadata_warning`
 * (R57/R59), then each `*_warning` key. Every merge here keeps that server
 * order. A list row carries `needs_acknowledgement`.
 *
 * FEATURE-DETECTED. An API or substrate that predates this sends neither key:
 * `readWarningItems` returns null and every surface
 * behaves exactly as before (the `has_warnings` "*", no Acknowledge links).
 *
 * FAIL SAFE. Nothing here ever invents an acknowledgement: lines are
 * acknowledged only by a server read or an acknowledge response (R50). And
 * acknowledging changes only the "*" (plus completed's amber/green) — never a
 * status label: a failed run is "Error*" then "Error", never "Completed".
 *
 * Pure: no React, no Office.js, no Remix server import.
 */
import { formatAbsolute } from "./formatTime";

export type RunWarningKind = "result_warning" | "early_exit" | "metadata_warning";

/** One acknowledgeable line, as the API publishes it (names, never OIDs). */
export interface RunWarningItem {
  id: string;
  kind: RunWarningKind | string;
  message: string;
  acknowledged: boolean;
  acknowledged_by_name: string | null;
  acknowledged_at: string | null;
}

/** POST …/warnings/{id}/acknowledge — the API's AcknowledgeRunWarningView. */
export interface AcknowledgeWarningResult {
  instance_id: string;
  warning_id: string;
  acknowledged_by_name: string | null;
  acknowledged_at: string | null;
  already_acknowledged: boolean;
  warning_items: RunWarningItem[];
  needs_acknowledgement: boolean;
}

export const NEEDS_ACKNOWLEDGEMENT_SUFFIX = "warnings need acknowledgement";
/** Any status (story 28633): a live run's lines are acknowledgeable too, so there is no "once it finishes" title. */
export const NEEDS_ACKNOWLEDGEMENT_TITLE =
  "Warnings need acknowledgement — open the run and acknowledge each one";
/** Today's tooltip for the has_warnings "*", kept for APIs that predate acknowledgement. */
export const LEGACY_WARNINGS_TITLE =
  "Completed with warnings — open the details for what needs attention";
export const EARLY_EXIT_PREFIX = "Ended early — ";

/** The legacy `metadata.warning` line's fixed id (substrate `METADATA_WARNING_ID`, R57/R59). */
export const METADATA_WARNING_ID = "metadata_warning";
/** The early-exit ("did not finish" / Ended early) line's fixed id (substrate `early_exit`). */
export const EARLY_EXIT_WARNING_ID = "early_exit";
export const ACKNOWLEDGE_LABEL = "Acknowledge";
export const ACKNOWLEDGING_LABEL = "Acknowledging…";
export const ACKNOWLEDGE_FAILED = "Couldn't acknowledge this warning. Try again.";
const SOMEONE = "someone";

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function readItem(raw: unknown): RunWarningItem | null {
  const r = obj(raw);
  if (!r || typeof r.id !== "string" || r.id === "" || typeof r.message !== "string") {
    return null;
  }
  const acknowledged = r.acknowledged === true;
  return {
    id: r.id,
    kind: typeof r.kind === "string" ? r.kind : "result_warning",
    message: r.message,
    acknowledged,
    acknowledged_by_name:
      acknowledged && typeof r.acknowledged_by_name === "string" ? r.acknowledged_by_name : null,
    acknowledged_at:
      acknowledged && typeof r.acknowledged_at === "string" ? r.acknowledged_at : null,
  };
}

/**
 * The run's lines, or null when the payload predates acknowledgement OR any
 * entry is malformed. A shorter list would read as "no warnings" / "all
 * acknowledged"; unknown makes callers fall back to the legacy marker (fail safe).
 */
export function readWarningItems(status: unknown): RunWarningItem[] | null {
  const raw = obj(status)?.warning_items;
  if (!Array.isArray(raw)) return null;
  const items = raw.map(readItem);
  return items.every((i): i is RunWarningItem => i !== null) ? items : null;
}

/**
 * Strict check of an acknowledge response (shared by ora's and the add-in's
 * clients): name and time each a string or null, ids strings, both flags
 * booleans, and every `warning_items` entry parses through `readWarningItems`
 * with the length unchanged (a dropped entry would read as "all acknowledged").
 */
export function isAcknowledgement(v: unknown): v is AcknowledgeWarningResult {
  if (!v || typeof v !== "object") return false;
  const a = v as Record<string, unknown>;
  if (!Array.isArray(a.warning_items)) return false;
  const items = readWarningItems({ warning_items: a.warning_items });
  if (items === null || items.length !== a.warning_items.length) return false;
  const stringOrNull = (x: unknown) => x === null || typeof x === "string";
  return (
    stringOrNull(a.acknowledged_by_name) &&
    stringOrNull(a.acknowledged_at) &&
    typeof a.instance_id === "string" &&
    typeof a.warning_id === "string" &&
    typeof a.already_acknowledged === "boolean" &&
    typeof a.needs_acknowledgement === "boolean"
  );
}

export function needsAcknowledgementOf(items: readonly RunWarningItem[]): boolean {
  return items.some((i) => !i.acknowledged);
}

/** `base`'s lines (order and text) with every acknowledgement `confirmed` reports for them — by id. */
export function mergeAcknowledgements(
  base: readonly RunWarningItem[],
  confirmed: readonly RunWarningItem[],
): RunWarningItem[] {
  const byId = new Map(
    confirmed.filter((i) => i.acknowledged).map((i) => [i.id, i] as const),
  );
  return base.map((item) => {
    if (item.acknowledged) return item;
    const other = byId.get(item.id);
    return other
      ? {
          ...item,
          acknowledged: true,
          acknowledged_by_name: other.acknowledged_by_name,
          acknowledged_at: other.acknowledged_at,
        }
      : item;
  });
}

/**
 * Ruling R50: the per-line monotonic union of server-confirmed lists. A line's
 * order and text come from the first list that has it; an acknowledgement in ANY
 * list stands, and the first one seen is kept. Safe because the server never
 * deletes an acknowledgement and records only the first acknowledger. Null when
 * there is no list at all ("unknown").
 */
export function unionWarningItems(
  lists: ReadonlyArray<readonly RunWarningItem[]>,
): RunWarningItem[] | null {
  if (lists.length === 0) return null;
  const byId = new Map<string, RunWarningItem>();
  for (const list of lists) {
    for (const item of list) {
      const seen = byId.get(item.id);
      if (!seen) {
        byId.set(item.id, item);
      } else if (!seen.acknowledged && item.acknowledged) {
        byId.set(item.id, {
          ...seen,
          acknowledged: true,
          acknowledged_by_name: item.acknowledged_by_name,
          acknowledged_at: item.acknowledged_at,
        });
      }
    }
  }
  return [...byId.values()];
}

/** A snapshot with the screen's confirmed lines folded in (for the step view and pill). Unchanged when the feature is absent. */
export function withAcknowledgements(
  status: unknown,
  items: readonly RunWarningItem[] | null,
): Record<string, unknown> | null {
  const s = obj(status);
  if (!s || !items) return s;
  return { ...s, warning_items: items, needs_acknowledgement: needsAcknowledgementOf(items) };
}

/** The finished statuses; statusMerge's TERMINAL_STATUSES is this same set. */
export const FINISHED_STATUSES = new Set(["completed", "failed", "cancelled", "timeout_cancelled", "rejected"]);

export interface WarningMarker {
  /** Render a "*" after the label. */
  asterisk: boolean;
  /** A completed run with an open line wears the amber pill instead of green. */
  amber: boolean;
  /** The whole pill's accessible name, when it differs from its text. */
  ariaLabel: string | null;
  title: string | null;
}

export function warningMarker({
  status,
  label,
  needsAcknowledgement,
  hasWarnings = false,
}: {
  status: string | null | undefined;
  label: string;
  needsAcknowledgement?: boolean | null;
  hasWarnings?: boolean;
}): WarningMarker {
  if (needsAcknowledgement === true) {
    return {
      asterisk: true,
      amber: (status ?? "").toLowerCase() === "completed",
      ariaLabel: `${label} — ${NEEDS_ACKNOWLEDGEMENT_SUFFIX}`,
      title: NEEDS_ACKNOWLEDGEMENT_TITLE,
    };
  }
  if (needsAcknowledgement === false) {
    return { asterisk: false, amber: false, ariaLabel: null, title: null };
  }
  return {
    asterisk: hasWarnings,
    amber: false,
    ariaLabel: null,
    title: hasWarnings ? LEGACY_WARNINGS_TITLE : null,
  };
}

/**
 * The pill's acknowledgement state, from the best server-confirmed source and
 * never from one about a different status (R50):
 *  - `live` (the hook: status reads + acknowledge responses) when it knows;
 *  - else a list row's flag, only when that row was read in the SAME status;
 *  - else, for a `completed` snapshot with no outcome fields at all (the window
 *    between a terminal push and the server read), `true` — "Completed*" until the
 *    read lands: a transient false warning, never a false all-clear;
 *  - else null: an API that predates acknowledgement (today's pill).
 */
export function pillNeedsAcknowledgement({
  status,
  live,
  row,
}: {
  status: unknown;
  live: boolean | null;
  row?: { status?: string | null; needsAcknowledgement?: boolean | null } | null;
}): boolean | null {
  if (live !== null) return live;
  const s = obj(status);
  const current = typeof s?.status === "string" ? s.status : null;
  const rowFlag = row?.needsAcknowledgement;
  if (typeof rowFlag === "boolean" && (current === null || row?.status === current)) {
    return rowFlag;
  }
  if (current === "completed" && s && s.ended_early === undefined && s.result_warnings === undefined) {
    return true;
  }
  return null;
}

export interface AcknowledgedSummary {
  names: string[];
  at: string | null;
}

/** Non-null only when the run HAS lines and every one is acknowledged. */
export function acknowledgedSummary(
  items: readonly RunWarningItem[] | null,
): AcknowledgedSummary | null {
  if (!items || items.length === 0 || needsAcknowledgementOf(items)) return null;
  const names = [...new Set(items.map((i) => i.acknowledged_by_name ?? SOMEONE))];
  const times = items
    .map((i) => i.acknowledged_at)
    .filter((t): t is string => typeof t === "string" && t !== "")
    .sort();
  return { names, at: times.length > 0 ? times[times.length - 1] : null };
}

export function acknowledgedByLine(
  item: Pick<RunWarningItem, "acknowledged_by_name" | "acknowledged_at">,
): string {
  const at = item.acknowledged_at ? ` · ${formatAbsolute(item.acknowledged_at)}` : "";
  return `Acknowledged by ${item.acknowledged_by_name ?? SOMEONE}${at}`;
}

export function acknowledgedSummaryLine(summary: AcknowledgedSummary): string {
  const at = summary.at ? ` · ${formatAbsolute(summary.at)}` : "";
  return `All warnings acknowledged by ${summary.names.join(", ")}${at}.`;
}

/**
 * The Done view's sentence when it shows the warning lines itself: null when the
 * sentence only repeats one of the lines (an ended-early run's sentence IS the
 * server's early-exit reason, which is also the `early_exit` line's message).
 * Any other sentence stays, so its warning copy is never lost.
 */
export function doneSentenceBesideLines(
  sentence: string | null | undefined,
  items: readonly RunWarningItem[] | null | undefined,
): string | null {
  if (!sentence) return null;
  const s = sentence.trim();
  return (items ?? []).some((i) => i.message.trim() === s) ? null : sentence;
}
